// Controlling a device over BLE when there is no Wi-Fi.
//
// Lives apart from bleClient because it now needs an AUTHENTICATED session, and
// bleSession imports the transport — putting this back in bleClient would make
// the two import each other.
//
// A CONNECTION IS HELD OPEN across taps. Every command otherwise pays for a
// scan, a connect, an MTU negotiation and a three-message handshake — several
// seconds in front of every button press. The session is cached per unique
// number and reused until it drops.
//
// The device advertises `Smartify<UniqueNumber>` while the app knows it by its
// unique number, so finding one means scanning and matching the name suffix.

import { BleClient, ScanMode } from '@capacitor-community/bluetooth-le';
import {
  ensureBleReady, bleAutoSupported, stopScan, isBleOffError, BLE_OFF,
} from './bleClient';
import { BLE_NAME_PREFIX, SERVICE_UUID, SCAN_TIMEOUT_MS } from './bleConfig';
import { openSession } from './bleSession';

let link = null; // { uniqueNumber, session }

export const connectedUniqueNumber = () => (link ? link.uniqueNumber : null);

// Status notifications from whichever device is currently linked, so a physical
// press still reaches the UI while offline.
const statusListeners = new Set();
export function onBleStatus(fn) {
  statusListeners.add(fn);
  return () => statusListeners.delete(fn);
}

async function findByUniqueNumber(uniqueNumber) {
  const target = String(uniqueNumber).toUpperCase();
  let found = null;
  // Offline control is the path where a switched-off radio is LEAST visible:
  // there is no scan list to look empty, only a button that does nothing. The
  // typed error propagates so the caller can say which of the two it was.
  await ensureBleReady();
  await BleClient.requestLEScan({
    services: [SERVICE_UUID],
    allowDuplicates: false,
    // Active scan — the name is in the scan response, and matching by unique
    // number needs the name.
    scanMode: ScanMode.SCAN_MODE_LOW_LATENCY,
  }, (result) => {
    const name = (result.localName || result.device?.name || '').toUpperCase();
    if (!name.startsWith(BLE_NAME_PREFIX.toUpperCase())) return;
    if (name.includes(target) && !found) found = result.device.deviceId;
  });
  // Poll rather than always waiting the full window: a device that answers in
  // 200 ms should not cost the user 15 seconds.
  const deadline = Date.now() + SCAN_TIMEOUT_MS;
  while (!found && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => { setTimeout(r, 250); });
  }
  await stopScan();
  return found;
}

/**
 * An authenticated session for this device, reusing the open one if there is
 * one. Returns null when the device cannot be found or authenticated.
 */
export async function ensureControlLink(uniqueNumber, ownerSecret = null) {
  // ⚠️ NATIVE ONLY, even though the transport now works on the web too.
  //
  // Everything here finds a device BY ITSELF: findByUniqueNumber scans and
  // matches a name, with nobody watching. The web has no such scan — the user
  // picks the device in a browser chooser, per connection — so on the web this
  // would put a permission dialog in front of every offline button press. A
  // control path that needs a click is not a fallback.
  if (!bleAutoSupported()) return null;
  const uno = String(uniqueNumber);

  if (link && link.uniqueNumber === uno && !link.session.isClosed()) return link.session;
  if (link) {
    await link.session.close();
    link = null;
  }

  const deviceId = await findByUniqueNumber(uno);
  if (!deviceId) return null;

  const opened = await openSession(deviceId, uno, {
    ownerSecret,
    onStatus: (text) => statusListeners.forEach((fn) => {
      try { fn(uno, text); } catch (e) { /* ignore listener error */ }
    }),
    onDisconnect: () => { link = null; },
  });
  if (!opened.ok) return null;

  link = { uniqueNumber: uno, session: opened.session };
  return opened.session;
}

/**
 * Send a control command over BLE.
 *
 * -> `{ ok, code }`. `code` is `BLE_OFF` when the radio is switched off, and
 * null for an ordinary "did not find it / did not authenticate".
 *
 * ⚠️ THE DISTINCTION IS THE WHOLE POINT. Both used to come back as plain
 * `false`, so a phone with Bluetooth turned off told the user "move closer and
 * try again" — advice that cannot possibly work, for a fault they could fix in
 * two seconds if anything had said what it was.
 */
export async function sendControlOverBle(uniqueNumber, message, ownerSecret = null) {
  try {
    const session = await ensureControlLink(uniqueNumber, ownerSecret);
    if (!session) return { ok: false, code: null };
    await session.send(message);
    return { ok: true, code: null };
  } catch (e) {
    return { ok: false, code: isBleOffError(e) ? BLE_OFF : null };
  }
}

/** Drop the held connection — call when leaving a device screen. */
export async function closeControlLink() {
  if (!link) return;
  await link.session.close();
  link = null;
}
